"use client";

import { useState, useEffect } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { UnifiedBookingPage } from "../booking";

export default function PublicBookingPage() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const [bookeeEmail, setBookeeEmail] = useState("");
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [eventType, setEventType] = useState<string | null>(null);
  const [eventTypeName, setEventTypeName] = useState<string | null>(null);
  const [eventTypeDescription, setEventTypeDescription] = useState<string | null>(null);
  const [groupEmails, setGroupEmails] = useState<string[]>([]);
  const [isLoadingEventType, setIsLoadingEventType] = useState<boolean>(false);
  const [prefilledName, setPrefilledName] = useState<string>("");
  const [prefilledEmail, setPrefilledEmail] = useState<string>("");
  const [presetDurations, setPresetDurations] = useState<number[]>([]);
  const [defaultLocation, setDefaultLocation] = useState<string>("");
  const [isLocationEditable, setIsLocationEditable] = useState<boolean>(true);
  const [isOnline, setIsOnline] = useState<boolean>(false);

  // Load event type config when eventType changes
  useEffect(() => {
    const eventTypeParam = searchParams?.get('eventType');
    
    if (eventTypeParam) {
      setEventType(eventTypeParam);
      setIsLoadingEventType(true);
      
      // Load event type config from API
      fetch(`/api/booking/event-types?eventType=${encodeURIComponent(eventTypeParam)}`)
        .then(res => res.json())
        .then(config => {
          if (config.error) {
            console.error('Error loading event type config:', config.error);
            setIsLoadingEventType(false);
            return;
          }
          
          setGroupEmails(config.groupEmails || []);
          setPresetDurations(config.availableDurations || []);
          setEventTypeName(config.name || null);
          setEventTypeDescription(config.description || null);
          if (config.defaultDuration) {
            setMeetingDuration(config.defaultDuration);
          }
          setDefaultLocation(config.location || '');
          setIsLocationEditable(false); // Lock location for event types
          setIsOnline(config.isOnline || false);
          setIsLoadingEventType(false);
        })
        .catch(error => {
          console.error('Error fetching event type config:', error);
          setIsLoadingEventType(false);
        });
    } else {
      // Reset event type state
      setEventType(null);
      setEventTypeName(null);
      setEventTypeDescription(null);
      setGroupEmails([]);
      setPresetDurations([]);
      setDefaultLocation('');
      setIsLocationEditable(true);
      setIsOnline(false);
      setIsLoadingEventType(false);
    }
  }, [searchParams]);

  // Handle URL parameters for pre-filled booking
  useEffect(() => {
    const emailParam = searchParams?.get('email');
    const durationParam = searchParams?.get('duration');
    const slotParam = searchParams?.get('slot');
    const nameParam = searchParams?.get('name');
    const eventTypeParam = searchParams?.get('eventType');
    
    if (emailParam) {
      setPrefilledEmail(emailParam);
      // If not in event type mode, also set bookeeEmail for backward compatibility
      if (!eventTypeParam) {
        setBookeeEmail(emailParam);
      }
    }
    
    if (nameParam) {
      setPrefilledName(nameParam);
    }
    
    if (durationParam) {
      const duration = parseInt(durationParam, 10);
      if (!isNaN(duration)) {
        setMeetingDuration(duration);
      }
    }

    if (slotParam) {
      setSelectedSlot(slotParam);
    }
  }, [searchParams]);

  // Update URL when selections change (only for non-event-type mode)
  const updateURL = (email: string, duration: number, slot?: string | null) => {
    if (eventType) {
      // In event type mode, don't update URL for email/duration changes
      // Only update slot if provided
      if (slot) {
        const params = new URLSearchParams(searchParams?.toString() || '');
        params.set('slot', slot);
        const newURL = params.toString() ? `${pathname || ''}?${params.toString()}` : (pathname || '');
        router.replace(newURL, { scroll: false });
      }
      return;
    }

    const params = new URLSearchParams();
    
    if (email) {
      params.set('email', email);
    }
    
    if (duration && duration !== 30) {
      params.set('duration', duration.toString());
    }

    if (slot) {
      params.set('slot', slot);
    }

    const newURL = params.toString() ? `${pathname || ''}?${params.toString()}` : (pathname || '');
    router.replace(newURL, { scroll: false });
  };

  const handleEmailChange = (email: string) => {
    if (!eventType) {
      setBookeeEmail(email);
      updateURL(email, meetingDuration, selectedSlot);
    }
  };

  const handleDurationChange = (duration: number) => {
    setMeetingDuration(duration);
    if (!eventType) {
      updateURL(bookeeEmail, duration, selectedSlot);
    }
  };

  const handleSlotSelect = (slot: string | null) => {
    setSelectedSlot(slot);
    updateURL(eventType ? '' : bookeeEmail, meetingDuration, slot);
  };

  // Public booking configuration
  // Hide person selector if eventType is set (even while loading config)
  const publicBookingConfig = {
    mode: 'public' as const,
    features: {
      showPersonSelector: !eventType && !isLoadingEventType, // Hide person selector in event type mode
      showDurationSelector: !eventType || presetDurations.length > 0, // Show if preset durations available
      allowCustomTimes: false,
      requireAuthentication: false,
    },
    ui: {
      theme: 'calendly' as const,
      showWeekNavigation: true,
      showTimeZoneSelector: false,
    },
  };

  return (
    <UnifiedBookingPage
      config={publicBookingConfig}
      bookeeEmail={eventType ? undefined : bookeeEmail}
      groupEmails={eventType ? groupEmails : undefined}
      eventType={eventType || undefined}
      eventTypeName={eventTypeName || undefined}
      eventTypeDescription={eventTypeDescription || undefined}
      meetingDuration={meetingDuration}
      presetDurations={presetDurations.length > 0 ? presetDurations : undefined}
      selectedSlot={selectedSlot}
      prefilledName={prefilledName || undefined}
      prefilledEmail={prefilledEmail || undefined}
      defaultLocation={defaultLocation || undefined}
      isLocationEditable={isLocationEditable}
      isOnline={isOnline}
      onEmailChange={handleEmailChange}
      onDurationChange={handleDurationChange}
      onSlotSelect={handleSlotSelect}
      onBookingSuccess={(eventId) => {
        console.log('Booking successful:', eventId);
      }}
    />
  );
}